import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

interface StripePrice {
  currency: string;
  interval: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  private readonly TRIAL_PERIOD_DAYS = 7;
  private readonly PRODUCTS_COLLECTION = 'products';
  private readonly CUSTOMERS_COLLECTION = 'customers';
  private readonly CUSTOMER_CHECKOUT_SESSIONS_COLLECTION = 'checkout_sessions';
  private readonly SUBSCRIPTIONS_COLLETION = 'subscriptions';
  private readonly PRODUCT_NAME = 'prefund';

  constructor(
    private db: AngularFirestore,
    private fns: AngularFireFunctions
  ) {}

  private async getPrices(): Promise<(StripePrice & { id: string })[]> {
    const querySnapshot = await this.db.firestore
      .collection(this.PRODUCTS_COLLECTION)
      .where('active', '==', true)
      .get();

    const pricePromises = querySnapshot.docs.map((productDoc) => {
      return productDoc.ref
        .collection('prices')
        .where('active', '==', true)
        .get()
        .then((priceCollectionRef) => {
          return priceCollectionRef.docs.map((doc) => {
            return { id: doc.id, ...(doc.data() as unknown as StripePrice) };
          });
        });
    });

    const prices: (StripePrice & { id: string })[] = (
      await Promise.all(pricePromises)
    ).flat();

    return prices;
  }

  async checkout(userId: string, interval: 'month' | 'year' = 'month') {
    const prices = await this.getPrices();

    const priceId = prices.find((p) => p.interval === interval)?.id;

    if (!priceId) {
      throw Error('Price was not found');
    }

    const docRef = await this.db.firestore
      .collection(this.CUSTOMERS_COLLECTION)
      .doc(userId)
      .collection(this.CUSTOMER_CHECKOUT_SESSIONS_COLLECTION)
      .add({
        price: priceId,
        trial_period_days: this.TRIAL_PERIOD_DAYS,
        success_url: `${window.location.origin}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/subscription-cancelled?session_id={CHECKOUT_SESSION_ID}`,
      });

    // Wait for the CheckoutSession to get attached by the extension
    docRef.onSnapshot((snap) => {
      // @ts-ignore
      const { error, url } = snap.data();
      if (error) {
        // Show an error to your customer and
        // inspect your Cloud Function logs in the Firebase console.
        alert(`An error occured: ${error.message}`);
      }
      if (url) {
        // We have a Stripe Checkout URL, let's redirect.

        window.location.assign(url);
      }
    });
  }

  async redirectToCustomerPortal() {
    const callable = this.fns.httpsCallable(
      'ext-firestore-stripe-payments-createPortalLink'
    ); // will use API key you set up when installing the extension

    const request = {
      returnUrl: window.location.origin,
      locale: 'auto', // Optional, defaults to "auto"
    };

    const data = await firstValueFrom(callable(request));
    window.location.assign(data.url);
  }

  async getSubscription(userId: string) {
    const querySnapshot = await this.db.firestore
      .collection(this.PRODUCTS_COLLECTION)
      .where('active', '==', true)
      .get();

    const pricePromises = querySnapshot.docs.map((productDoc) => {
      return productDoc.ref
        .collection('prices')
        .get()
        .then((priceCollectionRef) => {
          return priceCollectionRef.docs.map((doc) => {
            return { id: doc.id, ...(doc.data() as unknown as StripePrice) };
          });
        });
    });

    const prices: (StripePrice & { id: string })[] = (
      await Promise.all(pricePromises)
    ).flat();

    return prices;
  }
}
